import { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_ALL_YEARS, CHART_COLORS, formatChartTooltipValue, parseChartYearFilter } from '../../chartUtils';
import { useNewVsRenewalsQuery } from '../../hooks/useDashboardAnalytics';
import { ChartCard } from './ChartCard';
import { ChartYearFilter } from './ChartYearFilter';

type NewVsRenewalsChartProps = {
  regionalCode: string;
};

export function NewVsRenewalsChart({ regionalCode }: NewVsRenewalsChartProps) {
  const [yearFilter, setYearFilter] = useState(CHART_ALL_YEARS);
  const year = parseChartYearFilter(yearFilter);
  const { data, isLoading, errorMessage } = useNewVsRenewalsQuery(regionalCode, year);

  const chartData = useMemo(
    () =>
      (data?.items ?? [])
        .slice()
        .sort((a, b) => a.year - b.year)
        .map((item) => ({
          year: String(item.year),
          newApplication: item.newApplication,
          renewalApplication: item.renewalApplication,
        })),
    [data]
  );

  return (
    <ChartCard
      title="New Applications vs Renewals Per Year"
      subtitle="Stacked column · TSDHA total · use filters to drill down"
      toolbar={
        <ChartYearFilter
          value={yearFilter}
          onChange={setYearFilter}
          labelId="new-vs-renewals-year"
          includeAllYears
        />
      }
      loading={isLoading}
      error={errorMessage}
      minHeight={360}
    >
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="year" />
            <YAxis />
            <Tooltip formatter={(value) => formatChartTooltipValue(value)} />
            <Legend />
            <Bar
              dataKey="newApplication"
              name="New Applications"
              stackId="applications"
              fill={CHART_COLORS.forestGreen}
            />
            <Bar
              dataKey="renewalApplication"
              name="Renewal Applications"
              stackId="applications"
              fill={CHART_COLORS.gold}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : null}
    </ChartCard>
  );
}
